/**
 * SuggestedReplies — the clickable options of a `suggest_replies` tool call.
 *
 * `suggest_replies` is a Runtime no-op tool: the agent calls it as the last
 * action of a turn that asks the operator to pick one of a few options. It is
 * NOT a gate — the run does not park and the composer stays open, so typing
 * something else is an equally valid answer. A click sends the option's
 * `label` through the same path as typing (`handleSend`), so the transcript
 * shows the choice as an ordinary user message.
 *
 * Answered state is derived from the transcript, not stored: once any user
 * message follows the call, the card is spent (`suggestionAnswers`). That keeps
 * live and restored history identical — restored tool results are the
 * `{"restored":true}` stand-in, so nothing could be read from the result anyway.
 *
 * Design: CMHKAgentPlatformSpec `spec/tech/components/agui/2026-10-13-suggested-replies-design.md`.
 */

import { useState } from 'react';
import { SUGGEST_REPLIES_TOOL } from './hitlToolNames';
import { messageText, type TranscriptMessage } from './transcript';
import './SuggestedReplies.css';

export interface SuggestedReplyOption {
  label: string;
  description?: string;
}

/**
 * The text of the first user message after each `suggest_replies` call, by
 * toolCallId. A call with no entry is still open.
 *
 * Every open call is closed by the next user message, whatever it says — the
 * operator typing past the buttons answers them just as a click does.
 */
export function suggestionAnswers(messages: TranscriptMessage[]): Map<string, string> {
  const answers = new Map<string, string>();
  let open: string[] = [];
  for (const m of messages) {
    if (m.role === 'user') {
      const text = messageText(m.content).trim();
      for (const id of open) answers.set(id, text);
      open = [];
      continue;
    }
    if (m.role !== 'assistant') continue;
    for (const tc of m.toolCalls ?? []) {
      if (tc.function?.name === SUGGEST_REPLIES_TOOL) open.push(tc.id);
    }
  }
  return answers;
}

/** True for a tool result the Runtime returned as `{"error": …}` (invalid options). */
export function isSuggestRepliesError(result: string | undefined): boolean {
  if (!result) return false;
  try {
    const data = JSON.parse(result) as unknown;
    return !!data && typeof data === 'object' && 'error' in data;
  } catch {
    return false;
  }
}

/** Options as the model sent them, minus anything without a usable label. */
export function usableOptions(options: unknown): SuggestedReplyOption[] {
  if (!Array.isArray(options)) return [];
  return options.filter(
    (o): o is SuggestedReplyOption =>
      !!o && typeof o === 'object' && typeof (o as { label?: unknown }).label === 'string'
      && (o as { label: string }).label.trim() !== '',
  );
}

interface SuggestedRepliesProps {
  options: SuggestedReplyOption[];
  prompt?: string;
  /** The user message that followed this card, if any. `undefined` = still open. */
  answer?: string;
  /** Arguments still streaming — show the options, but not clickable yet. */
  pending?: boolean;
  onSelect: (label: string) => void;
}

export default function SuggestedReplies({ options, prompt, answer, pending, onSelect }: SuggestedRepliesProps) {
  // Set on click, before the message echoes back into the transcript — closes the
  // window where a second click (or a double-click) would send twice.
  const [clicked, setClicked] = useState<string | null>(null);
  const chosen = answer ?? clicked ?? undefined;
  const spent = chosen !== undefined;
  const disabled = spent || !!pending;

  if (options.length === 0) return null;

  return (
    <div className={`suggested-replies${spent ? ' suggested-replies--spent' : ''}`} data-testid="suggested-replies">
      {prompt ? <div className="suggested-replies__prompt">{prompt}</div> : null}
      <div className="suggested-replies__list">
        {options.map((opt) => {
          const label = opt.label.trim();
          const isChosen = spent && chosen === label;
          return (
            <button
              key={label}
              type="button"
              className={`suggested-replies__chip${isChosen ? ' suggested-replies__chip--chosen' : ''}`}
              disabled={disabled}
              aria-pressed={isChosen}
              title={opt.description || label}
              onClick={() => {
                if (disabled) return;
                setClicked(label);
                onSelect(label);
              }}
            >
              {isChosen ? <span className="suggested-replies__check" aria-hidden>✓ </span> : null}
              <span className="suggested-replies__label">{label}</span>
              {opt.description ? (
                <span className="suggested-replies__description">{opt.description}</span>
              ) : null}
            </button>
          );
        })}
      </div>
    </div>
  );
}
